import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Link2, RotateCcw, ScrollText, ThumbsDown, ThumbsUp } from 'lucide-react'
import Button from '../../components/Button'
import { useOnChange, useRemembered } from '../../components/listMemory'
import useListParams, { usePageParam } from '../../components/useListParams'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import DateRangeField from '../../components/DateRangeField'
import Drawer from '../../components/Drawer'
import FilterBar from '../../components/FilterBar'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import PageHeader from '../../components/PageHeader'
import GamificationNav from './GamificationNav'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { createReset, getTransactionsPage, listResetLogs, listTransactions } from './gamificationService'
import { resetLogsStrings as R, transactionsStrings as T } from './strings'
import './gamification.css'
import { serverText } from '../../lib/i18n'

/**
 * What this screen keeps in its own address — the reset the ledger is scoped to, and the
 * dates over it.
 *
 * `?resetLogId=` was already there; THE RANGE WAS PLAIN STATE, and it decides which rows
 * the page number is a page OF — so a Back out of a transaction returned to page 4 of the
 * whole ledger rather than page 4 of the fortnight being read, and a dated ledger could not
 * be sent to anyone. Empty means all time and stays out of the URL. Module-level, as
 * `useListParams` requires.
 */
const LIST_PARAMS = { resetLogId: '', from: '', to: '' }

/**
 * سجل العمليات + تصفير النقاط — the legacy `PointsTransactions` and `PointsResetLogs`.
 *
 * These were two menu entries over two views of one dataset, and a reset row linked *away*
 * from its own list into the other page with no way back. They are one page with two tabs
 * now; both legacy routes still resolve, each landing on its own tab, so any deep link keeps
 * working.
 *
 * Following a reset into the operations log applies its filter as a chip you can take off —
 * which returns you to the full log without navigating anywhere.
 *
 * THE LEDGER IS SERVER-PAGED and always was going to have to be: it is 5,800 rows here and
 * only grows, since a reset starts a new period rather than deleting anything.
 */
export default function PointsLogPage({ tab }) {
  const toast = useToast()
  const [params, setParams] = useListParams(LIST_PARAMS)
  const resetLogId = params.resetLogId
  /* Rebuilt from the two strings rather than used as it comes: `useListParams` hands back a
     fresh object whenever ANY parameter changes, the page included, and the fetch below
     depends on this identity — so paging alone would refetch. */
  const range = useMemo(() => ({ from: params.from, to: params.to }), [params.from, params.to])

  /* The rows carry the tab they were fetched FOR — see the note on `rows` below. */
  const [loaded, setLoaded] = useState({ tab: null, rows: [], total: 0 })
  const [loading, setLoading] = useState(true)
  /* The search is a working position, not an address — it would rewrite history on every
     keystroke — but it was plain state, so it was gone the moment a row was opened and the
     page it had narrowed came back showing something else. Keyed per tab: the two tabs
     search different lists. */
  const [query, setQuery] = useRemembered(`gamification-${tab}:query`, '')
  /* In the address, so Back and a refresh both land on the same page. */
  const [page, setPage] = usePageParam()
  /* How many rows the reader chose to see — not an address, but it was lost on every visit
     to a record, so a ledger set to 100 came back at 25. */
  const [pageSize, setPageSize] = useRemembered(`gamification-${tab}:pageSize`, 25)
  const [reloadKey, setReloadKey] = useState(0)

  /** The reset the ledger is scoped to, when it is — for the chip's label. */
  const [scopedReset, setScopedReset] = useState(null)
  const [creating, setCreating] = useState(false)
  const [notes, setNotes] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)

  /* A different tab is a different list; carrying the page number over would ask for page 4
     of a table with five rows. Neither the search nor the dates are cleared here any more:
     the search is remembered PER TAB, so clearing it would wipe the other tab's, and the
     dates live in the address, which the two routes already keep apart. */
  useOnChange(() => {
    setPage(1)
  }, [tab])

  /* A new search, a new range or a reset taken off is a new list, so it starts at its first
     page — on a CHANGE only, or the page carried in the address would be thrown away on
     arrival. The table cannot do this itself: the ledger is SERVER-paged, so the page
     belongs to this screen. */
  useOnChange(() => setPage(1), [query, range, resetLogId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const load =
      tab === 'transactions'
        ? listTransactions({ page, pageSize, query, from: range.from, to: range.to, resetLogId })
        : listResetLogs({ page, pageSize, query })

    load
      .then((response) => {
        if (cancelled) return
        setLoaded({ tab, rows: response.rows, total: response.total })
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoaded({ tab, rows: [], total: 0 })
        setLoading(false)
        toast.error(error?.message || T.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [tab, page, pageSize, query, range, resetLogId, reloadKey, toast])

  /* Which reset the chip is describing. Its own endpoint answers this — the ledger rows carry
     a reset id but the list may be empty, and a chip that disappears when its filter matches
     nothing is a filter you cannot remove. */
  useEffect(() => {
    if (!resetLogId) {
      setScopedReset(null)
      return undefined
    }
    let cancelled = false
    getTransactionsPage(resetLogId)
      .then((log) => !cancelled && setScopedReset(log))
      .catch(() => !cancelled && setScopedReset(null))
    return () => {
      cancelled = true
    }
  }, [resetLogId])


  const submitReset = async () => {
    setSaving(true)
    const result = await createReset(notes)
    setSaving(false)
    setConfirming(false)
    if (!result.ok) return toast.error(result.message || R.actionFailed)
    toast.success(serverText(result.message) || R.created)
    setCreating(false)
    setNotes('')
    setReloadKey((key) => key + 1)
    return undefined
  }

  const transactionColumns = useMemo(
    () => [
      {
        key: 'activity',
        header: T.columns.activity,
        sortable: false,
        width: '240px',
        render: (row) => (
          <span className="gm__activity">
            <b>
              <bdi>{row.activity}</bdi>
            </b>
          </span>
        ),
      },
      {
        key: 'username',
        header: T.columns.user,
        sortable: false,
        render: (row) => <bdi>{row.username}</bdi>,
      },
      {
        key: 'timestamp',
        header: T.columns.timestamp,
        sortable: false,
        /* Pre-formatted server-side as `dd-MM-yyyy hh:mm tt` in en-US — displayed as
           received (W6), never re-parsed: `new Date("09-08-2026 03:45 PM")` misreads it. */
        render: (row) => (
          <span className="cell-date" dir="ltr">
            {row.timestamp}
          </span>
        ),
      },
      {
        key: 'points',
        header: T.columns.points,
        sortable: false,
        align: 'center',
        /* The figure is already signed in the data, and it was printed in the same near-black
           either way — so the one thing the column is read for lived entirely in a glyph two
           cells over. The sign decides the tone rather than `isPositive`: they agree, and if
           they ever disagree the number is what is on screen. */
        render: (row) => (
          <span
            className={`gm__points gm__points--${row.points > 0 ? 'up' : row.points < 0 ? 'down' : 'nil'}`}
            dir="ltr"
          >
            {row.points.toLocaleString('en-US')}
          </span>
        ),
      },
      {
        // `T.columns.isPositive` does not exist in either locale — this header was blank.
        key: 'isPositive',
        header: T.columns.type,
        sortable: false,
        align: 'center',
        value: (row) => (row.isPositive ? T.positive : T.negative),
        render: (row) =>
          row.isPositive ? (
            <ThumbsUp size={16} strokeWidth={1.75} className="gm__good" />
          ) : (
            <ThumbsDown size={16} strokeWidth={1.75} className="gm__bad" />
          ),
      },
      {
        // Likewise `T.columns.effectOpposite`; the key is `opposite`.
        key: 'effectOpposite',
        header: T.columns.opposite,
        sortable: false,
        align: 'center',
        render: (row) => (row.effectOpposite ? '✓' : <span className="gm__nil">—</span>),
      },
      {
        key: 'resetLogDate',
        header: T.columns.resetDate,
        sortable: false,
        render: (row) => (
          <span className="cell-date" dir="ltr">
            {row.resetLogDate}
          </span>
        ),
      },
    ],
    [],
  )

  const resetColumns = useMemo(
    () => [
      {
        key: 'createdOn',
        header: R.columns.timestamp,
        sortable: false,
        render: (row) => (
          <span className="cell-date" dir="ltr">
            {row.createdOn}
          </span>
        ),
      },
      {
        key: 'createdBy',
        header: R.columns.user,
        sortable: false,
        /* The API sends this as an anchor built from a server-side `DomainURL` setting that is
           often empty. The name is taken and the markup dropped. */
        render: (row) => <bdi>{row.createdBy}</bdi>,
      },
      { key: 'notes', header: R.columns.notes, sortable: false, render: (row) => <bdi>{row.notes}</bdi> },
      {
        key: 'open',
        header: <Link2 size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '72px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <IconButton
            as={Link}
            to={`/gamification/transactions?resetLogId=${row.id}`}
            icon={ScrollText}
            label={R.viewTransactions}
          />
        ),
      },
    ],
    [],
  )

  const isLedger = tab === 'transactions'

  /* THE ROWS AND THE COLUMNS MUST AGREE, AT RENDER TIME.
   *
   * The two tabs share one table: `columns` follow the ROUTE, `rows` followed a fetch. So
   * clicking the other tab re-rendered with the new tab's columns and the OLD tab's rows,
   * and `row.points.toLocaleString()` threw on a reset-log row that has no `points` — the
   * whole screen went blank.
   *
   * Clearing the rows in an effect does NOT fix it: effects run after the commit, and the
   * throw happens during the render that precedes it. So the rows carry the tab they were
   * fetched for and are only handed to the table when the two match; a mismatch renders
   * the loading state instead, which is what the screen is doing anyway. This also covers
   * a slow response landing after the tab changed again. */
  const rows = loaded.tab === tab ? loaded.rows : []
  const total = loaded.tab === tab ? loaded.total : 0

  return (
    <>
      <PageHeader
        title={isLedger ? T.title : R.title}
        subtitle={isLedger ? T.subtitle : R.subtitle}
        count={loading ? undefined : total}
        actions={
          isLedger ? null : (
            <Button onClick={() => setCreating(true)}>
              <RotateCcw size={16} strokeWidth={1.75} />
              {R.create}
            </Button>
          )
        }
      />

      {/* The four screens, not the two this page happens to serve. */}
      <GamificationNav value={tab} />

      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: isLedger ? T.columns.user : R.columns.notes }}
        chips={
          scopedReset
            ? [
                {
                  id: 'reset',
                  label: R.title,
                  value: scopedReset.createdOn || String(scopedReset.id),
                  /* Takes the reset off and leaves the rest of the address alone. It used
                     to navigate to the bare path, which now takes the DATES down with it —
                     removing one filter must not clear another. */
                  onRemove: () => setParams({ resetLogId: '' }),
                },
              ]
            : []
        }
      >
        {isLedger && (
          /* `{from, to}` IS the patch `useListParams` takes, and it leaves `resetLogId`
             where it is. */
          <DateRangeField value={range} onChange={setParams} labels={{ from: T.from, to: T.to }} />
        )}
      </FilterBar>

      <DataTable
        columns={isLedger ? transactionColumns : resetColumns}
        rows={rows}
        loading={loading}
        query={query}
        getRowId={(row) => row.id}
        emptyMessage={isLedger ? T.empty : R.empty}
        server={{
          page,
          pageSize,
          total,
          onChange: (next) => {
            setPage(next.page)
            setPageSize(next.pageSize)
          },
        }}
      />

      {/* Creating a reset zeroes everyone's points and starts a new period. The note is the
          only thing recorded about WHY, so it is asked for before the confirm rather than
          after. */}
      <Drawer
      dialog
        open={creating}
        title={R.create}
        onClose={() => setCreating(false)}
        footer={
          <>
            <Button onClick={() => setConfirming(true)} disabled={saving}>
              {R.create}
            </Button>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              {T.close}
            </Button>
          </>
        }
      >
        <FormRow label={R.columns.notes} hint={R.notesHint}>
          {(id) => <TextField id={id} value={notes} onChange={setNotes} />}
        </FormRow>
      </Drawer>

      <ConfirmDialog
        open={confirming}
        title={R.createTitle}
        /* Irreversible, but not a delete — nothing is removed, a period is closed. Amber
           rather than the red the kit defaults to. */
        tone="warning"
        message={R.createConfirm}
        confirmLabel={R.create}
        cancelLabel={T.close}
        onConfirm={submitReset}
        onCancel={() => setConfirming(false)}
      />
    </>
  )
}
